import { formatCalendarDateTime } from '@cascade/core/utils';
import { useI18n } from '@cascade/react/i18n';
import { useTickets } from '@cascade/react/tickets';
import { Badge, Divider, Icon, Loading, Text } from '@cascade/ui-library';
import type { UIEvent } from 'react';
import { Link } from 'react-router-dom';
import { hasScrolledNearEnd, ticketStatusDisplays } from './Tickets.helpers.ts';
import classes from './Tickets.module.scss';
import { Page } from '#components/page/index.ts';
import { getTicketPath } from '#config/index.ts';

export const Tickets = () => {
  const { locale, translate, translateError } = useI18n();
  const {
    tickets,
    isLoadingTickets,
    ticketsError,
    isLoadingMoreTickets,
    loadMoreTickets,
  } = useTickets();

  const hasTickets = tickets.length > 0;
  const isEmpty = !hasTickets && !isLoadingTickets && !ticketsError;

  const loadMoreNearEnd = (event: UIEvent<HTMLElement>) => {
    if (!hasScrolledNearEnd(event.currentTarget)) {
      return;
    }

    loadMoreTickets();
  };

  return (
    <Page iconType='ticket' title={translate('tickets.title')}>
      <section
        className={classes.tickets}
        aria-label={translate('tickets.title')}
        onScroll={loadMoreNearEnd}
      >
        {ticketsError ? (
          <Text
            type='subtitle'
            weight='medium'
            color='text-danger'
            role='alert'
            content={translateError(ticketsError)}
            className={classes.status}
          />
        ) : null}
        {isEmpty ? (
          <Text
            type='subtitle'
            color='text-60'
            content={translate('tickets.none')}
            className={classes.status}
          />
        ) : null}
        {hasTickets ? (
          <ul className={classes.list}>
            {tickets.map(({ ticket, unread_count: unreadCount }, index) => {
              const { badgeVariant, iconType, iconColor, labelKey } =
                ticketStatusDisplays[ticket.status];

              const ticketPath = getTicketPath(ticket.display_id);
              const updatedAt = formatCalendarDateTime(ticket.updated_at, {
                locale,
              });

              return (
                <li key={ticket.display_id}>
                  {index > 0 ? <Divider emphasis='xs' /> : null}
                  <Link to={ticketPath} className={classes.row}>
                    <Icon
                      type={iconType}
                      size='lg'
                      color={iconColor}
                      className={classes.icon}
                    />
                    <div className={classes.details}>
                      <Text
                        type='h4'
                        weight='medium'
                        content={ticket.title}
                        truncated
                        compact
                      />
                      <div className={classes.meta}>
                        <Text
                          type='caption'
                          color='text-60'
                          content={ticket.display_id}
                        />
                        <Badge
                          label={translate(labelKey)}
                          variant={badgeVariant}
                          size='sm'
                        />
                        {unreadCount > 0 ? (
                          <Badge
                            label={translate('tickets.unread', {
                              count: unreadCount,
                            })}
                            variant='brand-darker'
                            size='sm'
                            iconType='circle'
                          />
                        ) : null}
                      </div>
                    </div>
                    <Text
                      type='caption'
                      color='text-60'
                      content={translate('tickets.updatedAt', {
                        date: updatedAt,
                      })}
                      className={classes.updated}
                    />
                    <Icon
                      type='chevron-right'
                      size='xs'
                      color='text-60'
                      className={classes.chevron}
                    />
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}
        {isLoadingTickets || isLoadingMoreTickets ? <Loading contain /> : null}
      </section>
    </Page>
  );
};
